import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Download, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { apiErr } from "../../../lib/apiErr";
import { useLatestRequest, isCanceled } from "../../../lib/useLatest";
import { SpendCard } from "./SpendCard";
import { Delta } from "./Delta";
import { MonthYearPicker, monthLabel, prevMonth, thisMonth } from "./MonthYearPicker";
import { downloadCsv, spendCsv } from "./spendCsv";

const sum = (d, k) => (d?.providers || []).reduce((s, p) => s + (p[k] || 0), 0);

// Monthly provider spend with previous-month comparison and CSV export. Reused by Model AI and Biaya AI pages.
export function SpendSection({ reloadKey }) {
  const [month, setMonth] = useState(thisMonth());
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  const latest = useLatestRequest();
  const load = useCallback(() => {
    setD(null); const signal = latest();
    const get = (m) => api.get("/admin/llm/spend", { params: { month: m }, signal }).then((r) => r.data);
    Promise.all([get(month), get(prevMonth(month)).catch(() => null)])
      .then(([cur, prev]) => { setDenied(false); setD({ cur, prev }); })
      .catch((e) => { if (isCanceled(e)) return; if (e?.response?.status === 403) { setDenied(true); setD({ cur: { providers: [] } }); return; } toast.error(apiErr(e, "Gagal memuat biaya")); setD({ cur: { providers: [] } }); });
  }, [month, latest]);
  useEffect(() => { load(); }, [load, reloadKey]);

  const prevBy = Object.fromEntries((d?.prev?.providers || []).map((p) => [p.provider, p]));
  const exportCsv = () => { downloadCsv(spendCsv(d.cur, d.prev), `biaya-ai-${d.cur.month || month}.csv`); toast.success("CSV diunduh"); };

  return (
    <section className="space-y-4" data-testid="spend-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-slate-900">Perkiraan biaya provider · {monthLabel(month)}</p>
          <p className="text-xs text-slate-500">Estimasi saja — cocokkan dengan konsol billing provider. Dibandingkan dengan {monthLabel(prevMonth(month))}.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <MonthYearPicker value={month} onChange={setMonth} />
          <button onClick={exportCsv} disabled={!d || denied || !d.cur?.providers?.length} className="btn-primary inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold disabled:opacity-50" data-testid="spend-export-csv"><Download size={13} /> Unduh CSV</button>
        </div>
      </div>
      {!d ? <Loader2 className="animate-spin text-slate-400" /> : denied ? (
        <div className="aivora-card flex items-start gap-3 p-5 text-sm text-slate-600" data-testid="spend-denied">
          <ShieldAlert size={18} className="mt-0.5 shrink-0 text-amber-500" />
          <div><p className="font-bold text-slate-900">Akses belum diizinkan server</p><p className="mt-0.5 text-xs">Server belum mengizinkan peran Anda membaca biaya AI (<code>GET /api/admin/llm/spend</code>). Minta tim backend menambahkan role finance & finance_staff.</p></div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600" data-testid="llm-spend-total">
            Total: <b className="text-slate-900">${sum(d.cur, "usd_own").toFixed(4)}</b> key sendiri
            <Delta now={sum(d.cur, "usd_own")} prev={d.prev ? sum(d.prev, "usd_own") : null} testid="spend-total-delta" />
            · <b className="text-amber-700">${sum(d.cur, "usd_emergent").toFixed(4)}</b> via Emergent
          </div>
          <div className="grid gap-3 md:grid-cols-2">{d.cur.providers.map((p) => <SpendCard key={p.provider} p={p} prev={d.prev ? prevBy[p.provider] || { usd_own: 0 } : null} thresholds={d.cur.thresholds || []} />)}</div>
        </>
      )}
    </section>
  );
}
